/**
 * Клиент API каркаса — §13.2: «обработка 401/403, единый экран отказа §6.4,
 * повторы».
 *
 * Три правила, из которых состоит весь файл:
 *
 * 1. Cookie сессии httpOnly, значит JavaScript её не видит и не может
 *    приложить руками — браузер прикладывает сам, если попросить
 *    `credentials: 'include'`. Никаких токенов в localStorage: то, что
 *    доступно скрипту, доступно и чужому скрипту.
 * 2. `401` и `403` — не «ошибка сети», а ответ по существу. Повторять их
 *    бессмысленно и вредно: человек увидит три задержки вместо одного
 *    честного экрана.
 * 3. Причину отказа сервер не сообщает (§6.4), и клиент её не выдумывает.
 *    Показывается один и тот же экран отказа.
 */

import { QueryClient } from '@tanstack/react-query';
import { getInitData } from './miniapp';

export class ApiError extends Error {
  readonly status: number;

  /**
   * Объяснение от сервера, если он его дал.
   *
   * Отказ в доступе не детализируется никогда (§6.4), и `detail` там
   * пуст. Но `400` — не про права, а про введённое человеком значение, и
   * сервер называет причину словами: «PDF без текстового слоя»,
   * «проходной балл ниже 70%». Раньше этот текст доходил до браузера и
   * выбрасывался, а человек читал «Запрос завершился со статусом 400» и
   * шёл искать поломку вместо того, чтобы исправить файл.
   */
  readonly detail: string | null;

  constructor(status: number, message: string, detail: string | null = null) {
    super(detail ?? message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }

  /** Нет сессии — нужно войти. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** Сессия есть, доступа нет — экран отказа §6.4. */
  get isForbidden(): boolean {
    return this.status === 403;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /**
   * Тело запроса как есть — файл, а не JSON.
   *
   * Загрузка файлов в проекте устроена одинаково у всех трёх сервисов:
   * одно поле, один файл, имя — заголовком (`X-Filename`). Разбора
   * `multipart/form-data` нет нигде, и заводить его ради одной кнопки
   * значило бы добавить зависимость на сервер ресторана и в два образа.
   * Здесь `raw` — единственное, чего каркасу для этого не хватало.
   */
  raw?: BodyInit;
  /** Дополнительные заголовки — имя и название файла при `raw`. */
  headers?: Record<string, string>;
}

/**
 * Режим показа без сервера: `VITE_MOCK_API=1`. Нужен ровно для одного —
 * чтобы человек, у которого не поднята вся инфраструктура, мог открыть
 * экран и посмотреть на него глазами. В боевой сборке ветка мертва.
 */
const MOCK = import.meta.env.VITE_MOCK_API === '1';

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  if (MOCK) {
    const { mockRequest } = await import('./mock');
    return mockRequest<T>(path, options);
  }

  const headers: Record<string, string> = { ...options.headers };
  if (options.body !== undefined) {
    headers['content-type'] = 'application/json';
  }
  // Mini App входит не по cookie, а по подписанной строке Telegram, и она
  // прикладывается к КАЖДОМУ запросу: сессии у Mini App нет (§6.2).
  // Заголовок безопасен и вне Telegram — там его просто нет, и сервер
  // проверяет cookie, как раньше.
  const initData = getInitData();
  if (initData) {
    headers['X-Telegram-Init-Data'] = initData;
  }

  const response = await fetch(path, {
    method: options.method ?? 'GET',
    // Без этого браузер не приложит cookie сессии, и любой запрос
    // окажется анонимным.
    credentials: 'include',
    headers: Object.keys(headers).length > 0 ? headers : undefined,
    body: options.raw ?? (options.body === undefined ? undefined : JSON.stringify(options.body)),
    signal: options.signal,
  });

  if (!response.ok) {
    throw new ApiError(
      response.status,
      `Запрос ${path} завершился со статусом ${response.status}`,
      await readDetail(response),
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

/**
 * Достать объяснение из тела отказа.
 *
 * Тело читается только у неуспешных ответов и только один раз. Ошибка
 * разбора здесь не поднимается: причина отказа — вещь полезная, но не
 * настолько, чтобы из-за испорченного JSON человек вместо честного
 * статуса увидел исключение парсера.
 */
async function readDetail(response: Response): Promise<string | null> {
  try {
    const body = (await response.json()) as { message?: unknown };
    return typeof body?.message === 'string' && body.message.trim() ? body.message : null;
  } catch {
    return null;
  }
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          // Отказ и неавторизованность повторять нельзя — ответ не
          // изменится, а человек будет ждать (см. правило 2 выше).
          if (error instanceof ApiError && (error.isForbidden || error.isUnauthorized)) {
            return false;
          }
          // Любой другой отказ 4xx — тоже: это ответ на то, что ввёл
          // человек («начало периода позже конца»), и повтор его не
          // изменит. А пока идут повторы, экран показывает «Загружаю…» без
          // полей, в которых ошибку можно исправить, — семь секунд
          // тупика вместо одной честной фразы.
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
            return false;
          }
          return failureCount < 2;
        },
        staleTime: 15_000,
        // Данные обновляются сами: при возвращении на вкладку и раз в
        // минуту, пока вкладка открыта (владелец, 23.09.2026: «я могу не
        // знать, что пришло что-то новое»). В фоне интервал не работает —
        // так устроен react-query, и это правильно: закрытая вкладка не
        // должна дёргать сервер. Экрану, которому нужно чаще, — свой
        // `refetchInterval` (приёмка и оплаты просят раз в 30 секунд).
        refetchOnWindowFocus: true,
        refetchInterval: 60_000,
      },
      mutations: { retry: false },
    },
  });
}

/* ── Типы ответов platform-api ─────────────────────────────────────── */

export interface LoginConfig {
  bot_username: string;
  restaurant_tz: string;
  login_available: boolean;
}

export interface Me {
  user_id: number;
  telegram_user_id: number;
  full_name: string | null;
  is_host: boolean;
  access: Record<string, string | null>;
}

export interface UserRow {
  user_id: number;
  full_name: string | null;
  position_id: number | null;
  position_name: string | null;
  employee_status: string | null;
  is_host: boolean;
  access: Record<string, string>;
}

export interface PendingRequest {
  id: number;
  /** Бот, в котором заявку подали. */
  service: string;
  /** Все сервисы, о которых человек просит: заявка одна на все боты (D187). */
  services: string[];
  applicant_name: string | null;
  applicant_note: string | null;
  requested_at: string;
}

export interface PositionOption {
  id: number;
  name: string;
  /** «Собственник» — все права, назначает только владелец (§8.7, D181). */
  is_owner?: boolean;
}

export interface UsersScreen {
  me: { user_id: number };
  services: string[];
  users: UserRow[];
  requests: PendingRequest[];
  employees_without_account: number;
  positions: PositionOption[];
}

/* ── Права внутри сервиса — §8.7, D181 ─────────────────────────────── */

export interface RightObject {
  service: string;
  code: string;
  title: string;
  hint: string;
  actions: string[];
  is_money: boolean;
}

export interface PositionRights extends PositionOption {
  /** По сервису: настроена ли должность и какие права — «объект:действие». */
  rights: Record<string, { configured: boolean; granted: string[] }>;
}

export interface RightsScreen {
  services: string[];
  objects: RightObject[];
  positions: PositionRights[];
}

export interface UserServiceRights {
  access_level: string | null;
  /** Откуда базовые права: host, owner, admin, position, default. */
  base: string;
  /** «объект:действие» → источник: host, owner, admin, position, default, added. */
  granted: Record<string, string>;
  /** Права, которые дала бы должность, но сняты лично. */
  removed: string[];
}

export interface UserRightsView {
  user_id: number;
  services: Record<string, UserServiceRights>;
}

export interface RightChange {
  service: string;
  object: string;
  action: string;
}

/** Сотрудник в панели «Права»: должность пометкой, права — по сервисам. */
export interface PersonRights {
  user_id: number;
  full_name: string | null;
  position_name: string | null;
  is_host: boolean;
  /** Должность «Собственник» — все права (§8.7). */
  is_owner: boolean;
  services: Record<string, UserServiceRights>;
}

export interface PeopleRightsScreen {
  services: string[];
  objects: RightObject[];
  people: PersonRights[];
}

export const api = {
  loginConfig: () => apiFetch<LoginConfig>('/api/auth/config'),
  me: () => apiFetch<Me>('/api/auth/me'),
  loginWithTelegram: (payload: Record<string, unknown>) =>
    apiFetch<Me>('/api/auth/telegram', { method: 'POST', body: payload }),
  logout: () => apiFetch<void>('/api/auth/logout', { method: 'POST' }),
  usersScreen: () => apiFetch<UsersScreen>('/api/platform/users'),
  setAccess: (userId: number, service: string, accessLevel: string) =>
    apiFetch<{ change: string }>(`/api/platform/users/${userId}/access`, {
      method: 'PUT',
      body: { service, access_level: accessLevel },
    }),
  revokeAccess: (userId: number, service: string) =>
    apiFetch<{ change: string }>(`/api/platform/users/${userId}/access/${service}`, {
      method: 'DELETE',
    }),
  approveRequest: (requestId: number, body: Record<string, unknown>) =>
    apiFetch<{ user_id: number }>(`/api/platform/access-requests/${requestId}/approve`, {
      method: 'POST',
      body,
    }),
  rejectRequest: (requestId: number, reason: string | null) =>
    apiFetch<{ status: string }>(`/api/platform/access-requests/${requestId}/reject`, {
      method: 'POST',
      body: { reason },
    }),
  invite: () => apiFetch<{ how: string }>('/api/platform/invite'),
  // Должность — не право доступа, и выдаётся она не так, как доступ:
  // одним значением на человека, а не парой «сервис + уровень».
  // `null` снимает должность: сотрудник между должностями — обычное
  // состояние.
  setPosition: (userId: number, positionId: number | null) =>
    apiFetch<{ position_id: number | null; opened?: string[] }>(
      `/api/platform/users/${userId}/position`,
      {
        method: 'PUT',
        body: { position_id: positionId },
      },
    ),
  // Права внутри сервиса (§8.7). Деньги и «Собственника» сервер проверяет
  // сам: серая галочка на экране — удобство, а не защита.
  rightsScreen: () => apiFetch<RightsScreen>('/api/platform/rights'),
  peopleRights: () => apiFetch<PeopleRightsScreen>('/api/platform/rights/people'),
  // Новая должность: с пустыми правами или слепком с другой (§8.7).
  createPosition: (body: { name: string; copy_from: number | null }) =>
    apiFetch<{ position_id: number; name: string; rights_copied: number }>(
      '/api/platform/positions',
      { method: 'POST', body },
    ),
  setPositionRight: (positionId: number, change: RightChange & { granted: boolean }) =>
    apiFetch<{ change: string }>(`/api/platform/positions/${positionId}/rights`, {
      method: 'PUT',
      body: change,
    }),
  userRights: (userId: number) =>
    apiFetch<UserRightsView>(`/api/platform/users/${userId}/rights`),
  // `granted: null` — снять личное исключение, то есть «как у должности».
  setUserRight: (userId: number, change: RightChange & { granted: boolean | null }) =>
    apiFetch<{ change: string }>(`/api/platform/users/${userId}/rights`, {
      method: 'PUT',
      body: change,
    }),
  resetUserRights: (userId: number, service?: string) =>
    apiFetch<{ removed: number }>(
      `/api/platform/users/${userId}/rights${service ? `?service=${service}` : ''}`,
      { method: 'DELETE' },
    ),
};
